import React, { useContext, useCallback, useMemo, useReducer } from 'react';
import { useEventEmitter } from '@ace/react-utilities';

const ADD_ACTION = "add";
const SET_ACTION = "set";
const SHOW_ACTION = "show";
const HIDE_ACTION = "hide";
const TOGGLE_ACTION = "toggle";
const SHOW_ALL_ACTION = "showall";
const HIDE_ALL_ACTION = "hideall";
const TOGGLE_ALL_ACTION = "toggleall";
const REFRESH_CARD_LAYOUT_ACTION = "refreshcardlayout";

export const COMPONENT_TOGGLE_EVENTS = {
    ON_TOGGLE: 'onToggle',
    ON_TOGGLE_ALL: 'onToggleAll',
    ON_REFRESH_CARD_LAYOUT: 'onRefreshCardLayout'
};

const hasOwnProperty = (target, propName) => Object.prototype.hasOwnProperty.call(target, propName);

const deepCopyState = (previousState) => {
    var state = JSON.parse(JSON.stringify(previousState));

    return state;
}

const updateState = (previousState, action, value) => {
    var state = deepCopyState(previousState);

    state[action.key] = value;

    return state;
}



const assignState = (previousState, action, value) => {
    if (!hasOwnProperty(previousState, action.key)) {
        return previousState;
    }

    return updateState(previousState, action, value);
}

const performAllAction = (previousState, action, show) => {
    var state = deepCopyState(previousState);
    var entries = Object.entries(state);
    entries.forEach((entry, index) => {
        let [key,] = entry;

        state[key] = show;
    });

    return state;
}

const addAction = (previousState, action) => {
    if (hasOwnProperty(previousState, action.key)) {
        return previousState;
    }

    return updateState(previousState, action, action.defaultValue || false);
};
const setAction = (previousState, action) => assignState(previousState, action, action.value);
const showAction = (previousState, action) => assignState(previousState, action, true);
const hideAction = (previousState, action) => assignState(previousState, action, false);
const toggleAction = (previousState, action) => assignState(previousState, action, !previousState[action.key]);
const showAllAction = (previousState, action) => performAllAction(previousState, action, true);
const hideAllAction = (previousState, action) => performAllAction(previousState, action, false);
const toggleAllAction = (previousState, action) => performAllAction(previousState, action, !previousState.isAllShown);
const refreshCardLayoutAction = (previousState, action) => assignState(previousState, action, true);


const ComponentToggleReducer = (previousState, action) => {
    switch (action.type) {
        case ADD_ACTION:
            return addAction(previousState, action);
        case SET_ACTION:
            return setAction(previousState, action);
        case SHOW_ACTION:
            return showAction(previousState, action);
        case HIDE_ACTION:
            return hideAction(previousState, action);
        case TOGGLE_ACTION:
            return toggleAction(previousState, action);
        case SHOW_ALL_ACTION:
            return showAllAction(previousState, action);
        case HIDE_ALL_ACTION:
            return hideAllAction(previousState, action);
        case TOGGLE_ALL_ACTION:
            return toggleAllAction(previousState, action);
        case REFRESH_CARD_LAYOUT_ACTION:
            return refreshCardLayoutAction(previousState, action);
        default:
            return previousState;
    }
}

const ComponentDisplayStateContext = React.createContext(null);
const ComponentToggleDispatchContext = React.createContext(null);

export function ComponentToggleContextProvider(props) {
    const applyEmitterHandler = useEventEmitter(props.componentName);
    const [componentsDisplayState, componentsToggleDispatch] = useReducer(ComponentToggleReducer, { isAllShown: false });

    const showComponent = useCallback((componentName) => {
        componentsToggleDispatch({
            type: SHOW_ACTION,
            key: componentName
        });
        
        applyEmitterHandler((emitter) => {
            emitter.emit(COMPONENT_TOGGLE_EVENTS.ON_TOGGLE, componentName);
        });
    }, [applyEmitterHandler]);

    const hideComponent = useCallback((componentName) => {
        componentsToggleDispatch({
            type: HIDE_ACTION,
            key: componentName
        });

        applyEmitterHandler((emitter) => {
            emitter.emit(COMPONENT_TOGGLE_EVENTS.ON_TOGGLE, componentName);
        });
    }, [applyEmitterHandler]);

    const toggleComponent = useCallback((componentName) => {
        componentsToggleDispatch({
            type: TOGGLE_ACTION,
            key: componentName
        });

        applyEmitterHandler((emitter) => {
            emitter.emit(COMPONENT_TOGGLE_EVENTS.ON_TOGGLE, componentName);
        });
    }, [applyEmitterHandler]);

    const addComponent = useCallback((componentName, defaultValue) => {
        componentsToggleDispatch({
            type: ADD_ACTION,
            key: componentName,
            defaultValue: defaultValue
        });
    }, []);

    const setComponent = useCallback((componentName, value) => {
        componentsToggleDispatch({
            type: SET_ACTION,
            key: componentName,
            value: value
        });

        applyEmitterHandler((emitter) => {
            emitter.emit(COMPONENT_TOGGLE_EVENTS.ON_TOGGLE, componentName);
        });
    }, [applyEmitterHandler]);

    const showAll = useCallback(() => {
        componentsToggleDispatch({
            type: SHOW_ALL_ACTION
        });

        applyEmitterHandler((emitter) => {
            emitter.emit(COMPONENT_TOGGLE_EVENTS.ON_TOGGLE_ALL);
        });
    }, [applyEmitterHandler]);

    const hideAll = useCallback(() => {
        componentsToggleDispatch({
            type: HIDE_ALL_ACTION
        });

        applyEmitterHandler((emitter) => {
            emitter.emit(COMPONENT_TOGGLE_EVENTS.ON_TOGGLE_ALL);
        });
    }, [applyEmitterHandler]);

    const toggleAll = useCallback(() => {
        componentsToggleDispatch({
            type: TOGGLE_ALL_ACTION
        });

        applyEmitterHandler((emitter) => {
            emitter.emit(COMPONENT_TOGGLE_EVENTS.ON_TOGGLE_ALL);
        });
    }, [applyEmitterHandler]);

    const refreshCardLayout = useCallback((componentName) => {
        componentsToggleDispatch({
            type: REFRESH_CARD_LAYOUT_ACTION,
            key: componentName
        });
        
        applyEmitterHandler((emitter) => {
            emitter.emit(COMPONENT_TOGGLE_EVENTS.ON_REFRESH_CARD_LAYOUT, componentName);
        });
    }, [applyEmitterHandler]);

    const content = useMemo(
        () => ({
            showComponent,
            hideComponent,
            toggleComponent,
            addComponent,
            setComponent,
            showAll,
            hideAll,
            toggleAll,
            refreshCardLayout
        }), 
        [showComponent, hideComponent, toggleComponent, addComponent, setComponent, showAll, hideAll, toggleAll, refreshCardLayout]
    );

    return (
        <ComponentDisplayStateContext.Provider value={componentsDisplayState} >
            <ComponentToggleDispatchContext.Provider value={content}>
                {props.children}
            </ComponentToggleDispatchContext.Provider>
        </ComponentDisplayStateContext.Provider>
    );
}

export function useComponentToggle(componentName) {
    const {
        toggleComponent,
        addComponent,
        setComponent,
        toggleAll,
        refreshCardLayout
    }  = useContext(ComponentToggleDispatchContext);

    const toggleComponentCB = useCallback(() => toggleComponent(componentName), [toggleComponent, componentName]);
    const addComponentCB = useCallback((defaultValue) => addComponent(componentName, defaultValue), [addComponent, componentName]);
    const setComponentCB = useCallback((value) => setComponent(componentName, value), [setComponent, componentName]);
    const toggleAllCB = useCallback(() => toggleAll(), [toggleAll]);
    const refreshCardLayoutCB = useCallback(() => refreshCardLayout(componentName), [refreshCardLayout, componentName])

    return useMemo(
        () => ({
            toggleComponent: toggleComponentCB,
            addComponent: addComponentCB,
            setComponent: setComponentCB,
            toggleAll: toggleAllCB,
            refreshCardLayout: refreshCardLayoutCB
        }), 
        [toggleComponentCB, addComponentCB, setComponentCB, toggleAllCB, refreshCardLayoutCB]
    );
}

export default function useComponentDisplayState(componentName) {
    const componentsDisplayState = useContext(ComponentDisplayStateContext);

    return componentsDisplayState[componentName];
}